/**
 * Compatibility mapper (anti-corruption layer).
 *
 * The `GET /tenantConfig` endpoint returns the legacy per-customer override shape
 * (`CustomerThemeOverrides`) that originated on `feat/customer-theme` /
 * `feat/SC-11525-cpq-branding-settings`. The current CPQ theme organises tokens
 * differently (`theme.novus.*`, `theme.palette.brand/accent/...`), so this module
 * translates the DB payload into the second `createTheme(...)` argument used by
 * `createAppTheme`. It is the ONLY place that knows both vocabularies.
 *
 * Design notes:
 *  - Values are written to BOTH the `novus.*` token and the derived `palette.*`
 *    entry, because the palette values are copied from the tokens at build time —
 *    overriding only the token would leave the palette stale.
 *  - `GENERAL_TOKEN_MAP` is the declarative 1:1 table (real-namespace key → current
 *    novus token + palette path). The backend writes these keys under
 *    `buttons` / `borders` / `pageBackgrounds` / `discount` / `typographyColours` /
 *    `colourPalette` — never a flat `other` bucket (no live payload has ever been
 *    observed to write one; a prior version of this file merged `overrides.other`
 *    in as a fallback for that reason, but it had zero real producers and was
 *    removed — along with the `other`-named variable/type that named it after that
 *    dead namespace). See the merged `generalTokens` variable in
 *    `mapTenantThemeToOverrides` below, which just combines the six real namespaces.
 *    Brand/accent/heading and namespace-specific tokens are handled separately.
 *  - `equipmentCard` and `addOnCard` ARE mapped (step 10b) -- their MuiCard variants
 *    read `tokens.equipmentCard*` / `tokens.addOnCard*`. This was not always true: the
 *    variants once used hardcoded literals, and the pair was dropped here for that
 *    reason. Two fields stay unmapped because they still have no consumer --
 *    `addOnCard.boxShadow` and `addOnCard.discountBadgeBg`.
 *  - `scheduler` is still DROPPED: there is no scheduler component in this package
 *    (the TimeScheduler is external), so there is no token to drive.
 *  - Unmapped keys in the merged general-purpose table above are also dropped.
 *
 * Phase-out: once the backend returns the current theme structure natively, this
 * file is deleted and `createAppTheme` consumes the response directly.
 */

import { merge } from 'lodash';
import type { PaletteColorOptions } from '@mui/material/styles';
import type {
  CategoryBorderMap,
  CustomerThemeOverrides,
  GeneralTokenOverrides,
} from '@services/tenantConfig.types';

/** Partial second-argument shape for `createTheme` (current theme namespaces). */
export type AppThemeOverrides = {
  novus?: Record<string, unknown>;
  palette?: Record<string, unknown>;
  colors?: Record<string, unknown>;
  boxShadows?: Record<string, unknown>;
  companyColors?: Record<string, unknown>;
  typography?: Record<string, unknown>;
};

/** Path into the current theme's palette: [group, field]. */
type PalettePath = readonly [group: string, field: string];

/** A general-purpose token's target on the current theme. */
type GeneralMapping = { novus?: string; palette?: PalettePath };

/**
 * Direct 1:1 translations from the merged general-purpose token bucket (see
 * `generalTokens` below) to the current theme. Target token names verified against
 * `novus.tokens.ts`; palette paths against the augmentation in
 * `theme.declarations.ts` / the palette in `theme.ts`. Brand/accent/heading and
 * tokens that moved to dedicated namespaces (topNavBar, shoppingCartDrawer,
 * checkAvailabilityDialog, charges, orderSummary) are handled in their own blocks
 * below.
 */
const GENERAL_TOKEN_MAP: Record<string, GeneralMapping> = {
  // Action buttons
  // selectPlanBg, selectPlanColor, checkoutBg, checkoutHoverBg: NOT mapped. Their
  // novus tokens/palette aliases have no consumer anywhere — the Select Plan and
  // Checkout buttons actually paint from `primaryBrandBlue`/`sectionBgWhite`
  // (theme.ts's shared button styles), never from these tokens. Mapping them would
  // silently do nothing, the same reasoning as the borders.* fields left unmapped below.
  selectPlanHoverBg: { novus: 'selectPlanHoverBg', palette: ['interactive', 'selectPlanHover'] },
  checkoutDisabledBg: { novus: 'checkoutDisabledBg', palette: ['interactive', 'checkoutDisabled'] },
  editPlanColor: { novus: 'editPlanColor', palette: ['interactive', 'editPlanText'] },
  editPlanBg: { novus: 'editPlanBg', palette: ['interactive', 'editPlanBg'] },
  editPlanBorder: { novus: 'editPlanBorder', palette: ['border', 'editPlan'] },
  // Real consumer at novus.tokens.ts's editPlan button variant (hover state) — had no
  // mapping at all before, so a tenant's buttons.editPlanHoverBg/editPlanHoverColor
  // silently never reached it.
  editPlanHoverBg: { novus: 'editPlanHoverBg' },
  editPlanHoverColor: { novus: 'editPlanHoverColor' },
  // Section / surface backgrounds
  // bgSectionAlt: NOT mapped — sectionBgBlue/palette.surface.sectionBlue have no consumer.
  surfaceWhite: { novus: 'sectionBgWhite', palette: ['surface', 'white'] },
  // Real field name is `bgAccentLight` (in `pageBackgrounds`) — not `bgAccentLighter`.
  // Precedence: checkAvailabilityDialog.bgAccentLight (step 4, below) writes this SAME
  // novus.bgCyanLight token and runs after this table, so when both fields are set on
  // a tenant, checkAvailabilityDialog.bgAccentLight wins. Deliberate — checkAvailability
  // is the more specific/newer namespace for this token; kept here only for tenants that
  // set pageBackgrounds.bgAccentLight without touching checkAvailabilityDialog.
  bgAccentLight: { novus: 'bgCyanLighter', palette: ['surface', 'cyanLighter'] },
  bgAccentDark: { novus: 'bgCyanDark', palette: ['surface', 'cyanDark'] },
  dialogBg: { novus: 'dialogBg', palette: ['surface', 'dialog'] },
  // Address header text/gradient
  // addressHeaderColor: the only style consumer (theme.ts's CheckoutHeader-title
  // branch) now reads theme.novus.addressHeaderColor (tenant-merged), so this mapping
  // is live again — see the fix there.
  addressHeaderColor: { novus: 'addressHeaderColor', palette: ['text', 'onAddressHeader'] },
  // addressHeaderGradient, changeAddressBtnColor, changeAddressBtnHoverBg: NOT mapped —
  // each is only ever read back out of the static novusTokens import into palette.* at
  // theme.ts module-load time; no component reads palette.surface.addressHeaderGradient
  // or palette.accent.changeAddress(Hover). Same reasoning as the borders.* fields below.
  // Borders + text
  textMuted: { novus: 'mutedText', palette: ['text', 'muted'] },
  // borderDefault, borderHover: NOT mapped — defaultBorder/defaultHoverBorder and their
  // palette.border.default/defaultHover aliases have no consumer.
  borderDisabled: { novus: 'borderDisabled', palette: ['border', 'disabled'] },
  // Links
  colorLink: { novus: 'linkColor', palette: ['interactive', 'link'] },
  colorLinkHover: { novus: 'linkHoverColor', palette: ['interactive', 'linkHover'] },
  // Discount / feedback
  bgDiscount: { novus: 'discountBadgeBg', palette: ['feedback', 'discountBg'] },
  colorDiscountTag: { novus: 'discountTagColor', palette: ['feedback', 'discountTag'] },
  // colorDiscountMessage, discountBadgeBorder: NOT mapped — discountMessageColor and
  // discountBadgeBorder have no style consumer (the discountBadge variant only reads
  // discountBadgeBg for its background; there is no border or message-text rule wired
  // to these tokens today).
  colorError: { novus: 'errorRed', palette: ['feedback', 'error'] },
  warningDialogIconColor: { novus: 'warningDialogIconColor', palette: ['feedback', 'warningDialogIcon'] },
  // Real consumer: an error-banner backgroundColor in novus.tokens.ts. Had no mapping
  // at all — a tenant's borders.bgError silently never reached it.
  bgError: { novus: 'errorBannerBg', palette: ['feedback', 'errorBannerBg'] },
  // Real consumer: the `infoBanner` MuiPaper variant's backgroundColor already reads
  // tokens.bgCyanLight (distinct from bgCyanLighter above, which pageBackgrounds.
  // bgAccentLight/checkAvailabilityDialog.bgAccentLight drive) — but nothing mapped
  // borders.infoBannerBg to it.
  infoBannerBg: { novus: 'bgCyanLight', palette: ['surface', 'cyanLight'] },
  // Real consumer: the orderConfirmationCard variant's border in novus.tokens.ts reads
  // tokens.orderConfirmationCardBorder — config field is named confirmationCardBorder.
  confirmationCardBorder: { novus: 'orderConfirmationCardBorder' },
  // Real consumer: the orderConfirmationPage variant's backgroundColor in novus.tokens.ts
  // reads tokens.orderConfirmationBg — config field is named confirmationPageBg.
  confirmationPageBg: { novus: 'orderConfirmationBg' },
  // `borders.separatorBorder`, `borders.infoBannerBorder`, and a flat `borders.borderSelected`
  // are intentionally NOT mapped here — none has any current UI consumer to wire to
  // (the infoBanner variant has no border property at all; selected-state borders are
  // only ever per-category, e.g. planCardBorderSelectedInternet). Mapping them to an
  // unrelated existing token would be guessing at a design intent nothing today expresses.
  // Real consumer: CheckoutPageWrapper (Checkout.styles.ts) reads theme.novus.checkoutPageBg.
  // Console field: CPQ Branding → Page & Backgrounds → "Checkout Page Background".
  checkoutPageBg: { novus: 'checkoutPageBg' },
  // Real consumers: MuiRadio checked colour (theme.ts) and TextFieldLabel's "(Optional)" text.
  colorCheckbox: { novus: 'checkboxColor' },
  textSubtle: { novus: 'textSubtle' },
  // Hardcoded-colour cleanup: each token below already had a real consumer but no field
  // reached it. Config field names are new (console must expose them).
  // Real consumer: MuiButton text colour + other purple-text sites (novus.tokens.ts).
  colorButtonText: { novus: 'novusPurpleColor', palette: ['purple', 'text'] },
  // Real consumer: the checkoutDrawer button background (rest + disabled).
  checkoutDrawerButtonBg: { novus: 'purplePrimary', palette: ['purple', 'primary'] },
  // Real consumer: the errorBannerIcon variant colour.
  colorErrorIcon: { novus: 'errorIcon', palette: ['feedback', 'errorIcon'] },
  // Real consumers: serviceAddressCard + technicianNoteBox borders.
  serviceCardBorder: { novus: 'serviceCardBorder' },
  // Real consumers: filePreviewCard border + TVChannelLogos outline (palette.border.channelLogo).
  channelLogoBorder: { novus: 'channelLogoBorder', palette: ['border', 'channelLogo'] },
  // Real consumer: TVChannelLogos hover outline (palette.border.channelLogoHover).
  channelLogoBorderHover: { novus: 'channelLogoBorderHover', palette: ['border', 'channelLogoHover'] },
  // Real consumer: filePreviewCard thumbnail background.
  fileThumbnailBg: { novus: 'fileThumbnailBg' },
  // Literal cleanup: shadows/colours that were typed straight into style overrides.
  checkoutDrawerDisabledColor: { novus: 'checkoutDrawerDisabledColor' },
  themePackageCardShadow: { novus: 'themePackageCardShadow' },
  individualChannelCardShadow: { novus: 'individualChannelCardShadow' },
  dialogActionsShadow: { novus: 'dialogActionsShadow' },
  warningDialogShadow: { novus: 'warningDialogShadow' },
  autocompleteOptionBg: { novus: 'autocompleteOptionBg' },
  inputBorderColor: { novus: 'inputBorderColor' },
  autocompleteShadow: { novus: 'autocompleteShadow' },
  // NOT mapped (no consumer): purpleCheck/palette.purple.check, novusPurpleHoverBg,
  // cyanSelectedBorder, placeholderBg.
  // `pageBackgrounds.infoBackground` is NOT mapped — no current consumer.
};

// PaletteColorOptions is a union — only the SimplePaletteColorOptions branch has
// `main`. Narrow with an `in` check rather than casting the union away.
const readMain = (color: PaletteColorOptions | undefined): string | undefined =>
  color && 'main' in color ? color.main : undefined;

// Runtime guard: only treat a value as a CategoryBorderMap when it is an object.
const asCategoryMap = (value: unknown): CategoryBorderMap | undefined =>
  value && typeof value === 'object' ? (value as CategoryBorderMap) : undefined;

export const mapTenantThemeToOverrides = (
  overrides: CustomerThemeOverrides = {},
): AppThemeOverrides => {
  // The theme editor/backend write these fields under six separate real namespaces
  // (never a flat `other` bucket — see the module docstring) — merge them all so
  // GENERAL_TOKEN_MAP's lookups work regardless of which namespace a given key
  // actually lives in.
  //
  // Each of these six is typed as an open `Record<string, string | undefined>`
  // (tenantConfig.types.ts) — nothing in the type system (or here) stops the same key
  // name from appearing in two of them with different values; the later argument wins
  // silently (colourPalette last). No such collision is known to exist in any real
  // payload today, but this can't be verified statically from the frontend alone — it
  // depends on what the theme editor/backend actually write. If a config field ever
  // stops taking effect unexpectedly, check here first for a same-name key in a
  // different namespace than expected.
  const generalTokens = merge(
    {},
    overrides.buttons,
    overrides.borders,
    overrides.pageBackgrounds,
    overrides.discount,
    overrides.typographyColours,
    overrides.colourPalette,
  ) as GeneralTokenOverrides;
  const planCard = overrides.planCard ?? {};
  const topNavBar = overrides.topNavBar ?? {};
  const checkAvailabilityDialog = overrides.checkAvailabilityDialog ?? {};
  const cartItems = overrides.cartItems ?? {};
  const charges = overrides.charges ?? {};
  // `cartDrawer` (surface/border fields: itemBorder, expandableBg, surfaceWhite,
  // verticalDivider, expandableSectionBg, totalLabelColor) and `shoppingCartDrawer`
  // (footer/hover fields: cartFooterBg, cartFooterColor, btnPurpleHoverBg) are two
  // distinct real namespaces in themeConfig — merge both so either's fields resolve
  // regardless of which one a given field actually lives in.
  const shoppingCartDrawer = merge({}, overrides.shoppingCartDrawer, overrides.cartDrawer);
  const orderSummary = overrides.orderSummary ?? {};
  const mui = overrides.muiOptions;

  const novus: Record<string, unknown> = {};
  const palette: Record<string, Record<string, unknown>> = {};
  const setPalette = (group: string, field: string, value: unknown): void => {
    (palette[group] ??= {})[field] = value;
  };

  // 1. Table-driven direct token translations from the merged general-purpose bucket.
  Object.entries(GENERAL_TOKEN_MAP).forEach(([key, mapping]) => {
    const value = generalTokens[key];
    if (typeof value !== 'string') return;
    if (mapping.novus) novus[mapping.novus] = value;
    if (mapping.palette) setPalette(mapping.palette[0], mapping.palette[1], value);
  });

  // 2. Semantic brand / accent / heading from the merged general-purpose bucket.
  const heading = generalTokens.colorSectionHeading;
  if (heading) {
    novus.headingBlue = heading;
    setPalette('brand', 'heading', heading);
  }
  const accent = generalTokens.colorBrandAccent ?? generalTokens.colorAccent;
  if (accent) {
    novus.accentCyan = accent;
    novus.highlightIconColor = accent;
    setPalette('accent', 'cyan', accent);
    setPalette('accent', 'highlightIcon', accent);
  }

  // 3. Top nav bar: brand colour, cart hover bg, cart count colour.
  //    `topNavBar.colorBrand` takes precedence; falls back to muiOptions.palette.primary.main.
  const brand = topNavBar.colorBrand ?? readMain(mui?.palette?.primary);
  if (brand) {
    novus.primaryBrandBlue = brand;
    setPalette('brand', 'primary', brand);
  }
  if (topNavBar.navBarCartHoverBg) {
    novus.navBarCartHoverBg = topNavBar.navBarCartHoverBg;
    setPalette('interactive', 'navCartHover', topNavBar.navBarCartHoverBg);
  }
  if (topNavBar.cartCountColor) {
    novus.cyanAccent = topNavBar.cartCountColor;
    setPalette('accent', 'cyanAccent', topNavBar.cartCountColor);
  }

  // 4. Check-availability dialog tokens.
  if (checkAvailabilityDialog.colorBrandAccentBright) {
    novus.accentCyanBright = checkAvailabilityDialog.colorBrandAccentBright;
    setPalette('accent', 'cyanBright', checkAvailabilityDialog.colorBrandAccentBright);
  }
  if (checkAvailabilityDialog.bgAccentLight) {
    novus.bgCyanLight = checkAvailabilityDialog.bgAccentLight;
    setPalette('surface', 'cyanLight', checkAvailabilityDialog.bgAccentLight);
  }
  if (checkAvailabilityDialog.bgWarning) {
    novus.warningBg = checkAvailabilityDialog.bgWarning;
    setPalette('feedback', 'warningBg', checkAvailabilityDialog.bgWarning);
  }
  if (checkAvailabilityDialog.colorWarningIcon) {
    novus.warningIconColor = checkAvailabilityDialog.colorWarningIcon;
    setPalette('feedback', 'warningIcon', checkAvailabilityDialog.colorWarningIcon);
  }

  // 5. Cart items tokens.
  if (cartItems.dividerColor) novus.headerBorder = cartItems.dividerColor;
  if (cartItems.quantityCounterBorder) novus.quantityCounterBorder = cartItems.quantityCounterBorder;

  // 6. Charges/pricing tokens.
  if (charges.sectionDivider) novus.cartSectionDivider = charges.sectionDivider;
  if (charges.quantityLabelColor) novus.quantityLabelColor = charges.quantityLabelColor;
  if (charges.discountGreen) {
    novus.discountGreen = charges.discountGreen;
    setPalette('feedback', 'discountGreen', charges.discountGreen);
  }

  // 7. Shopping cart drawer tokens.
  if (shoppingCartDrawer.cartFooterBg) {
    novus.cartFooterBg = shoppingCartDrawer.cartFooterBg;
    setPalette('surface', 'cartFooter', shoppingCartDrawer.cartFooterBg);
    // The cartDrawerFooter variant hardcoded white and nothing read cartFooterBg
    // outside the palette alias above, so this field was inert. cartFooterSurfaceBg
    // is the token the variant actually paints from.
    novus.cartFooterSurfaceBg = shoppingCartDrawer.cartFooterBg;
  }
  if (shoppingCartDrawer.cartFooterColor) {
    novus.cartFooterColor = shoppingCartDrawer.cartFooterColor;
    setPalette('text', 'onCartFooter', shoppingCartDrawer.cartFooterColor);
    novus.cartFooterTextColor = shoppingCartDrawer.cartFooterColor;
  }
  if (shoppingCartDrawer.btnPurpleHoverBg) novus.btnPurpleHoverBg = shoppingCartDrawer.btnPurpleHoverBg;
  if (shoppingCartDrawer.surfaceWhite) novus.cartSurfaceWhite = shoppingCartDrawer.surfaceWhite;
  if (shoppingCartDrawer.itemBorder) novus.cartItemBorder = shoppingCartDrawer.itemBorder;
  if (shoppingCartDrawer.expandableBg) novus.cartExpandableBg = shoppingCartDrawer.expandableBg;
  if (shoppingCartDrawer.verticalDivider) novus.cartVerticalDivider = shoppingCartDrawer.verticalDivider;
  if (shoppingCartDrawer.expandableSectionBg) novus.cartExpandableSectionBg = shoppingCartDrawer.expandableSectionBg;
  if (shoppingCartDrawer.totalLabelColor) novus.cartTotalLabelColor = shoppingCartDrawer.totalLabelColor;

  // 8. Order summary tokens.
  if (orderSummary.couponAppliedAccent) novus.couponAppliedAccent = orderSummary.couponAppliedAccent;
  if (orderSummary.orderSummaryDivider) {
    novus.orderSummaryDivider = orderSummary.orderSummaryDivider;
    setPalette('accent', 'orderSummaryDivider', orderSummary.orderSummaryDivider);
  }

  // 9. Address-header section overrides. `headingBlue` runs after
  //    typographyColours.colorSectionHeading (via the merged general-purpose bucket above)
  //    so an explicit `addressHeader.headingBlue` takes precedence. `novusPurpleBg` also
  //    drives the derived outlined-border token.
  const addressHeader = overrides.addressHeader;
  if (addressHeader) {
    const {
      bgNavBar,
      btnPurpleBg,
      headingBlue,
      addressCardBg,
      novusPurpleBg,
      novusPurpleContainedHoverBg,
      borderBlack,
      warningText,
      btnPurpleCtaHoverBg,
      btnTextColor,
      addressHeaderColor,
    } = addressHeader;
    if (bgNavBar) {
      novus.addressHeaderBg = bgNavBar;
      setPalette('surface', 'addressHeader', bgNavBar);
    }
    if (btnPurpleBg) novus.btnPurpleBg = btnPurpleBg;
    if (headingBlue) {
      novus.headingBlue = headingBlue;
      setPalette('brand', 'heading', headingBlue);
    }
    if (addressCardBg) novus.addressCardBg = addressCardBg;
    if (novusPurpleBg) {
      novus.novusPurpleBg = novusPurpleBg;
      novus.novusPurpleOutlinedBorder = `1px solid ${novusPurpleBg}`;
    }
    if (novusPurpleContainedHoverBg) novus.novusPurpleContainedHoverBg = novusPurpleContainedHoverBg;
    if (borderBlack) {
      novus.borderBlack = borderBlack;
      setPalette('border', 'black', borderBlack);
    }
    if (warningText) {
      novus.warningText = warningText;
      setPalette('feedback', 'warningText', warningText);
    }
    if (btnPurpleCtaHoverBg) novus.btnPurpleCtaHoverBg = btnPurpleCtaHoverBg;
    if (btnTextColor) novus.btnTextColor = btnTextColor;
    // The console writes this field to the `addressHeader` namespace, but the only
    // mapping for it lived in GENERAL_TOKEN_MAP, which is looked up against the six
    // merged general-purpose buckets (buttons/borders/pageBackgrounds/discount/
    // typographyColours/colourPalette) -- `addressHeader` is not one of them, so the
    // value never arrived and the field silently did nothing. The token itself has
    // real consumers (theme.ts's CheckoutHeader-title branch + palette.text.onAddressHeader),
    // so reading it here is all that was missing. The GENERAL_TOKEN_MAP entry stays
    // for any tenant that wrote it into one of those buckets; this block runs later
    // and therefore wins, which is the namespace the console actually writes.
    if (addressHeaderColor) {
      novus.addressHeaderColor = addressHeaderColor;
      setPalette('text', 'onAddressHeader', addressHeaderColor);
    }
  }

  // 10. Plan-card styling tokens — flat fields + per-category selection.
  if (planCard.borderRadius) novus.planCardBorderRadius = planCard.borderRadius;
  if (planCard.padding) novus.planCardPadding = planCard.padding;
  if (planCard.borderDefault) novus.planCardBorderDefault = planCard.borderDefault;
  if (planCard.boxShadow) novus.planCardBoxShadow = planCard.boxShadow;
  if (planCard.boxShadowHover) novus.planCardBoxShadowHover = planCard.boxShadowHover;
  // Writes `stepCardShadow`, not `planCardStepCardShadow`: the real token behind the
  // checkoutStepCard variant's box-shadow is `novus.stepCardShadow`. The old name had
  // no consumer anywhere, so this field silently did nothing.
  if (planCard.stepCardShadow) novus.stepCardShadow = planCard.stepCardShadow;

  const borderSelected = asCategoryMap(planCard.borderSelected);
  if (borderSelected) {
    if (borderSelected.Internet) novus.planCardBorderSelectedInternet = borderSelected.Internet;
    if (borderSelected.Video) novus.planCardBorderSelectedVideo = borderSelected.Video;
    if (borderSelected.Phone) novus.planCardBorderSelectedPhone = borderSelected.Phone;
  }
  const selectedBadgeBg = asCategoryMap(planCard.selectedBadgeBg);
  if (selectedBadgeBg) {
    if (selectedBadgeBg.Internet) novus.planCardBadgeBgInternet = selectedBadgeBg.Internet;
    if (selectedBadgeBg.Video) novus.planCardBadgeBgVideo = selectedBadgeBg.Video;
    if (selectedBadgeBg.Phone) novus.planCardBadgeBgPhone = selectedBadgeBg.Phone;
  }

  // 10b. Equipment-card and add-on-card styling tokens.
  //
  // These two namespaces used to be dropped (see this module's docstring): when the
  // mapper was written the card variants painted from hardcoded literals, so there
  // was nothing to map onto. They now read `tokens.equipmentCard*` / `tokens.addOnCard*`
  // in `buildNovusComponentOverrides` (the equipmentCard and addOnCard MuiCard
  // variants), so every field below has a real consumer.
  //
  // `addOnCard.boxShadow` and `addOnCard.discountBadgeBg` are still NOT mapped: the
  // addOnCard variant sets no box-shadow and has no discount-badge rule, so there is
  // no token to drive. Wiring them would mean inventing a visual the design does not
  // have today (and changing the no-tenant default). They are dropped from the
  // console form instead.
  const cardTokens = (
    source: Record<string, unknown> | undefined,
    fieldToToken: Record<string, string>,
  ): void => {
    if (!source) return;
    Object.entries(fieldToToken).forEach(([field, token]) => {
      const value = source[field];
      if (typeof value === 'string' && value) novus[token] = value;
    });
  };

  cardTokens(overrides.equipmentCard, {
    radius: 'equipmentCardRadius',
    padding: 'equipmentCardPadding',
    borderDefault: 'equipmentCardBorderDefault',
    borderSelected: 'equipmentCardBorderSelected',
    // `borderHover` drives TWO tokens. `equipmentCardBorderHover` is read by the
    // themePackageCard variant and is kept so tenants that already set this field do
    // not lose that. `equipmentCardHoverOutline` is what the equipment card itself
    // reads -- it hovered to a hardcoded navy, so "Hover Border" changed a different
    // component than its label implies. Same story for addOnCard below.
    borderHover: 'equipmentCardBorderHover',
  });
  cardTokens(overrides.equipmentCard, { borderHover: 'equipmentCardHoverOutline' });
  cardTokens(overrides.addOnCard, {
    radius: 'addOnCardRadius',
    padding: 'addOnCardPadding',
    borderDefault: 'addOnCardBorderDefault',
    borderSelected: 'addOnCardBorderSelected',
    borderHover: 'addOnCardBorderHover',
  });
  cardTokens(overrides.addOnCard, { borderHover: 'addOnCardHoverOutline' });

  // 11. Raw MUI palette (primary/secondary/text) supplied via muiOptions.
  if (mui?.palette) merge(palette, mui.palette);

  // Typography: base body font from muiOptions.typography.fontFamily or the
  // bodyFont stack. `typography` may be a function on ThemeOptions — only read
  // and merge it when it is a plain object.
  const muiTypography =
    mui?.typography && typeof mui.typography === 'object' ? mui.typography : undefined;
  const typography: Record<string, unknown> = {};
  // fontStacks.bodyFont is only the fallback: when muiTypography carries its own
  // fontFamily, the merge below always overwrites it with that value anyway.
  if (overrides.fontStacks?.bodyFont) typography.fontFamily = overrides.fontStacks.bodyFont;
  if (muiTypography) merge(typography, muiTypography);

  const hasKeys = (o: Record<string, unknown>): boolean => Object.keys(o).length > 0;

  const result: AppThemeOverrides = {};
  if (hasKeys(novus)) result.novus = novus;
  if (hasKeys(palette)) result.palette = palette;
  if (overrides.colors) result.colors = overrides.colors;
  if (overrides.boxShadows) result.boxShadows = overrides.boxShadows;
  if (overrides.companyColors) result.companyColors = overrides.companyColors;
  if (hasKeys(typography)) result.typography = typography;
  return result;
};
